import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Alert, Paper, Stack, alpha } from "@mui/material";

import PageContainer from "../layouts/PageContainer";
import { getDashboardStats } from "../redux/actions/dashboardActions";
import { openTab } from "../redux/actions/workspaceActions";
import {
  getEntityMeta,
  getSidebarItemByEntity,
} from "../helpers/dashboardHelpers";

// Import Widgets
import SalesWidget from "../components/dashboard/widgets/SalesWidget";
import ItemWidget from "../components/dashboard/widgets/ItemWidget";
import UserStatusWidget from "../components/dashboard/widgets/UserStatusWidget";
import CustomerWidget from "../components/dashboard/widgets/CustomerWidget";
import SimpleStatWidget from "../components/dashboard/widgets/SimpleStatWidget";
import AuditLogWidget from "../components/dashboard/widgets/AuditLogWidget";
import SmsOutboxWidget from "../components/dashboard/widgets/SmsOutboxWidget";
import AnnouncementOverviewWidget from "../components/dashboard/widgets/AnnouncementOverviewWidget";
import ReceiptWidget from "../components/dashboard/widgets/ReceiptWidget";
import InvoiceWidget from "../components/dashboard/widgets/InvoiceWidget";
import DebtWidget from "../components/dashboard/widgets/DebtWidget";
import PurchaseOrderWidget from "../components/dashboard/widgets/PurchaseOrderWidget";
import CustomerOrderWidget from "../components/dashboard/widgets/CustomerOrderWidget";
import CreditNoteWidget from "../components/dashboard/widgets/CreditNoteWidget";
import QuotationWidget from "../components/dashboard/widgets/QuotationWidget";
import PaymentWidget from "../components/dashboard/widgets/PaymentWidget";
import AccountWidget from "../components/dashboard/widgets/AccountWidget";
import LedgerEntryWidget from "../components/dashboard/widgets/LedgerEntryWidget";
import InventoryLogWidget from "../components/dashboard/widgets/InventoryLogWidget";
import NotificationOverviewWidget from "../components/dashboard/widgets/NotificationOverviewWidget";
import AppCircularProgress from "../components/common/AppCircularProgress";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { EntityType } from "../shared/enums/EntityType";

const DashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { stats, loadingStats, errorLoadingStats } = useAppSelector(
    (state) => state.dashboard,
  );
  const { userInfo } = useAppSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getDashboardStats(12));
  }, [dispatch]);

  // --- TABBED WORKSPACE NAVIGATION ---
  const handleSuggestionClick = (entityKey: string): void => {
    const sidebarItem = getSidebarItemByEntity(entityKey);

    if (sidebarItem && sidebarItem.componentKey) {
      dispatch(
        openTab({
          id: sidebarItem.path,
          title: sidebarItem.title,
          path: sidebarItem.path,
          componentKey: sidebarItem.componentKey,
        }),
      );
      navigate(sidebarItem.path);
    }
  };

  if (loadingStats) {
    return (
      <PageContainer title="Dashboard">
        <AppCircularProgress />
      </PageContainer>
    );
  }

  if (errorLoadingStats) {
    return (
      <PageContainer title="Dashboard">
        <Alert severity="error">{errorLoadingStats}</Alert>
      </PageContainer>
    );
  }

  const { suggestedLinks = [], metrics = {} } = stats || {};

  const renderWidget = (entityKey: EntityType, data: any): React.ReactNode => {
    switch (entityKey) {
      case EntityType.SALE:
        return <SalesWidget data={data} />;
      case EntityType.ITEM:
        return <ItemWidget data={data} />;
      case EntityType.USER:
        return <UserStatusWidget data={data} />;
      case EntityType.CUSTOMER:
        return <CustomerWidget data={data} />;
      case EntityType.AUDIT_LOG:
        return <AuditLogWidget data={data} />;
      case EntityType.SMS_OUTBOX:
        return <SmsOutboxWidget data={data} />;
      case EntityType.ANNOUNCEMENT:
        return <AnnouncementOverviewWidget data={data} />;
      case EntityType.RECEIPT:
        return <ReceiptWidget data={data} />;
      case EntityType.INVOICE:
        return <InvoiceWidget data={data} />;
      case EntityType.DEBT:
        return <DebtWidget data={data} />;
      case EntityType.PURCHASE_ORDER:
        return <PurchaseOrderWidget data={data} />;
      case EntityType.CUSTOMER_ORDER:
        return <CustomerOrderWidget data={data} />;
      case EntityType.CREDIT_NOTE:
        return <CreditNoteWidget data={data} />;
      case EntityType.QUOTATION:
        return <QuotationWidget data={data} />;
      case EntityType.PAYMENT:
        return <PaymentWidget data={data} />;
      case EntityType.ACCOUNT:
        return <AccountWidget data={data} />;
      case EntityType.LEDGER_ENTRY:
        return <LedgerEntryWidget data={data} />;
      case EntityType.INVENTORY_LOG:
        return <InventoryLogWidget data={data} />;
      case EntityType.NOTIFICATION:
        return <NotificationOverviewWidget data={data} />;
      // Fallback for BRANCH, SUPPLIER, SMS_TEMPLATE, etc.
      default:
        return <SimpleStatWidget entityType={entityKey} data={data} />;
    }
  };

  return (
    <PageContainer
      title={`Welcome back, ${userInfo?.firstName || "User"}!`}
      subtitle="Here is an overview of your workspace activity and statistics."
    >
      {/* 1. SUGGESTED LINKS (Scrollable Row) */}
      {suggestedLinks.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "text.secondary", mb: 2 }}
          >
            Suggested Modules
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            {suggestedLinks.map((entityKey) => {
              const meta = getEntityMeta(entityKey);
              const Icon = meta.icon;
              const hasRoute = !!getSidebarItemByEntity(entityKey);

              return (
                <Paper
                  key={entityKey}
                  elevation={0}
                  onClick={() => hasRoute && handleSuggestionClick(entityKey)}
                  sx={{
                    minWidth: 180,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    cursor: hasRoute ? "pointer" : "default",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    transition: "all 0.2s ease",
                    "&:hover": hasRoute
                      ? {
                          borderColor: meta.color,
                          boxShadow: `0 4px 12px ${alpha(meta.color, 0.2)}`,
                          transform: "translateY(-2px)",
                        }
                      : {},
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: alpha(meta.color, 0.1),
                      color: meta.color,
                      display: "flex",
                    }}
                  >
                    <Icon />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold" }}
                    noWrap
                  >
                    {meta.title}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* 2. METRICS GRID */}
      {Object.keys(metrics).length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          {Object.entries(metrics).map(([entityKey, data]) => (
            <Box key={entityKey}>
              {renderWidget(entityKey as EntityType, data)}
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <Typography sx={{ color: "text.secondary" }}>
            No statistics available yet.
          </Typography>
        </Box>
      )}
    </PageContainer>
  );
};

export default DashboardPage;
